import { useState, useRef, useEffect, memo } from 'react';
import { SceneElement } from '../types';
import { Mailbox } from './mailbox';
import styles from './draggable-text-element.module.css';

interface DraggableMailboxElementProps {
  element: SceneElement;
  containerWidth: number;
  containerHeight: number;
  isSelected: boolean;
  onSelect: () => void;
  onPositionChange: (elementId: string, position: { x: number; y: number; width?: number; height?: number }) => void;
}

export const DraggableMailboxElement = memo(({
  element,
  containerWidth,
  containerHeight,
  isSelected,
  onSelect,
  onPositionChange,
}: DraggableMailboxElementProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0, elementX: 0, elementY: 0 });
  const resizeStartPos = useRef({ x: 0, y: 0, width: 0, height: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).classList.contains(styles.resizeHandle)) {
      return; // Let resize handle its own event
    }
    
    // Don't start drag if clicking on mailbox buttons
    const target = e.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.closest('button')) {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    onSelect();
    
    setIsDragging(true);
    dragStartPos.current = {
      x: e.clientX,
      y: e.clientY,
      elementX: element.position.x,
      elementY: element.position.y,
    };
  };

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect();
    
    setIsResizing(true);
    resizeStartPos.current = {
      x: e.clientX,
      y: e.clientY,
      width: element.position.width || 50,
      height: element.position.height || 70,
    };
  };

  useEffect(() => {
    if (!isDragging && !isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const deltaX = e.clientX - dragStartPos.current.x;
        const deltaY = e.clientY - dragStartPos.current.y;
        
        // Convert pixel delta to percentage
        const deltaXPercent = (deltaX / containerWidth) * 100;
        const deltaYPercent = (deltaY / containerHeight) * 100;
        
        let newX = dragStartPos.current.elementX + deltaXPercent;
        let newY = dragStartPos.current.elementY + deltaYPercent;
        
        // With transform: translate(-50%, -50%), elements are centered
        // So they can go from 0% to 100% without going out of bounds
        newX = Math.max(0, Math.min(100, newX));
        newY = Math.max(0, Math.min(100, newY));
        
        onPositionChange(element.id, {
          x: Math.round(newX * 10) / 10,
          y: Math.round(newY * 10) / 10,
          width: element.position.width,
          height: element.position.height,
        });
      } else if (isResizing) {
        const deltaX = e.clientX - resizeStartPos.current.x;
        const deltaY = e.clientY - resizeStartPos.current.y;
        
        // Convert pixel delta to percentage
        const deltaWidthPercent = (deltaX / containerWidth) * 100;
        const deltaHeightPercent = (deltaY / containerHeight) * 100;
        
        let newWidth = resizeStartPos.current.width + deltaWidthPercent;
        let newHeight = resizeStartPos.current.height + deltaHeightPercent;
        
        // Minimum size constraints for mailbox
        newWidth = Math.max(30, newWidth);
        newHeight = Math.max(40, newHeight);
        
        onPositionChange(element.id, {
          x: element.position.x,
          y: element.position.y,
          width: Math.round(newWidth * 10) / 10,
          height: Math.round(newHeight * 10) / 10,
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, element, containerWidth, containerHeight, onPositionChange]);

  if (!element.mailbox) {
    return null;
  }
  
  return (
    <div
      className={`${styles.draggableElement} ${isSelected ? styles.selected : ''} ${isDragging ? styles.dragging : ''}`}
      style={{
        left: `${element.position.x}%`,
        top: `${element.position.y}%`,
        width: element.position.width ? `${element.position.width}%` : 'auto',
        height: element.position.height ? `${element.position.height}%` : 'auto',
      }}
      onMouseDown={handleMouseDown}
    >
      <Mailbox
        from={element.mailbox.from}
        to={element.mailbox.to}
        subject={element.mailbox.subject}
        content={element.mailbox.content}
        metadata={element.mailbox.metadata}
        onReply={() => {}} // Disabled in admin mode
        onArchive={() => {}} // Disabled in admin mode
        onReport={() => {}} // Disabled in admin mode
        onLinkClick={undefined} // Disabled in admin mode
      />
      {isSelected && (
        <div 
          className={styles.resizeHandle}
          onMouseDown={handleResizeMouseDown}
          title="Drag to resize"
        />
      )}
    </div>
  );
});

DraggableMailboxElement.displayName = 'DraggableMailboxElement';
